import type { PillTone } from '../../components/StatusPill';
import type { LongDateParts } from './dateOrdinal';

// =======================================
// BLOCK TYPES
// =======================================

/**
 * Available block types for the document builder
 */
export type BlockType = 
  | 'heading'
  | 'paragraph'
  | 'scope_list'
  | 'bullet_list'
  | 'table'
  | 'image'
  | 'image_appendix'
  | 'signature'
  | 'divider'
  | 'spacer'
  // Site Instruction only. Deliberately absent from SlashCommandMenu's options
  // so "/" cannot create a second one -- see LOCKED_BLOCK_TYPES.
  | 'instruction_list'
  // RWD Design Certificate only, and likewise absent from the slash menu.
  | 'drawing_list'
  // RWC As Constructed Certificate only, and likewise absent from the slash menu.
  | 'qa_list'
  // Appendix-page blocks (see APPENDIX_BLOCK_TYPES). Inserted from the sidebar's
  // Insert panel, never from the slash menu: they own whole pages after the
  // letter rather than sitting in its flow.
  | 'table_appendix'
  | 'plan_appendix'
  | 'pdf_attachment'
  // A blank page between appendices, for whatever the report needs there.
  // Not page-owning: it is a marker in the body flow that starts a new page
  // and names the pages that follow, so the text put on it paginates exactly
  // as the letter's does.
  | 'blank_page';

/**
 * Block type display information
 */
export const BLOCK_TYPE_INFO: Record<BlockType, { label: string; icon: string; description: string }> = {
  heading: { label: 'Heading', icon: 'H', description: 'Add a heading (H1, H2, H3)' },
  paragraph: { label: 'Paragraph', icon: 'P', description: 'Add a text paragraph' },
  scope_list: { label: 'Scope List', icon: '☰', description: 'Notion-style interactive list' },
  bullet_list: { label: 'Bullet List', icon: '•', description: 'Simple bullet list' },
  table: { label: 'Table', icon: '▦', description: 'Add an editable table' },
  image: { label: 'Image', icon: '🖼', description: 'Add a single image' },
  image_appendix: { label: 'Image Appendix', icon: '📷', description: 'Numbered image grid' },
  signature: { label: 'Signature', icon: '✍', description: 'Document signature block' },
  divider: { label: 'Divider', icon: '—', description: 'Horizontal line separator' },
  spacer: { label: 'Spacer', icon: '↕', description: 'Add vertical space' },
  instruction_list: { label: 'Instructions', icon: '№', description: 'Numbered site instruction rows' },
  drawing_list: { label: 'Drawing List', icon: '▤', description: 'Certified drawings and design criteria' },
  qa_list: { label: 'QA Items', icon: '☑', description: 'Quality assurance results relied on' },
  table_appendix: { label: 'Table appendix', icon: '▦', description: 'Inspection record or schedule of works' },
  plan_appendix: { label: 'Location plan', icon: '◎', description: 'Plan with numbered location markers' },
  pdf_attachment: { label: 'Attached PDF', icon: '📎', description: 'Pages of an uploaded PDF' },
  blank_page: { label: 'Blank page', icon: '▯', description: 'An empty page to write on, between appendices' }
};

/**
 * Blocks that own whole PAGES after the letter body rather than sitting in its
 * flow: the photo appendix, the table appendix, the location plan and an
 * attached PDF. The paginator gives each of them its own page(s) and never
 * measures them against the body's content width; the body canvases filter
 * them out; the Insert panel is the only way to add one.
 *
 * Membership is not cosmetic. A block missing from this set is placed in the
 * letter's flow and measured at the letter's width, which for any of these is
 * simply the wrong page.
 */
export const APPENDIX_BLOCK_TYPES: ReadonlySet<BlockType> = new Set<BlockType>([
  'image_appendix',
  'table_appendix',
  'plan_appendix',
  'pdf_attachment',
]);

/**
 * Where an appendix SECTION starts: a page-owning block, or a blank page's
 * marker. A section runs from its start to the next one, so it carries the
 * body blocks after it -- what is written on a blank page, or the letter's
 * flow continued after an appendix. Moving, and the order the rail shows, work
 * on whole sections: moving only the start block would leave a blank page's
 * text behind on whatever page came before it.
 */
export const startsAppendixSection = (block: Block): boolean =>
  APPENDIX_BLOCK_TYPES.has(block.type) || block.type === 'blank_page';

/**
 * Where the LETTER ends: the index of the first appendix section (a
 * page-owning block or a blank page), or the end of the content when there
 * is none. Anything that belongs to the letter -- a paragraph from the Insert
 * panel, the signature block the sign prompt adds -- goes in here, never at
 * the end of the content: appended after the appendices it lands on an
 * untitled "Continued" page after the last of them.
 */
export const letterEndIndex = (content: Block[]): number => {
  const at = content.findIndex(startsAppendixSection);
  return at === -1 ? content.length : at;
};

/** The ids of the blocks that start each appendix section, in order. */
export const appendixSectionIds = (content: Block[]): string[] =>
  content.filter(startsAppendixSection).map(b => b.id);

/**
 * Swap the section `blockId` starts with its neighbour in `direction`. The
 * letter before the first section never moves. Returns the content unchanged
 * when there is no neighbour.
 */
export const moveAppendixSection = (
  content: Block[],
  blockId: string,
  direction: 'up' | 'down'
): Block[] => {
  const starts = content
    .map((b, i) => (startsAppendixSection(b) ? i : -1))
    .filter(i => i !== -1);
  const at = starts.findIndex(i => content[i].id === blockId);
  const other = direction === 'up' ? at - 1 : at + 1;
  if (at === -1 || other < 0 || other >= starts.length) return content;
  const sections = starts.map((start, n) => content.slice(start, starts[n + 1] ?? content.length));
  [sections[at], sections[other]] = [sections[other], sections[at]];
  return [...content.slice(0, starts[0]), ...sections.flat()];
};

/**
 * Blocks a template owns rather than the user.
 *
 * They are part of the form itself, so the canvas hides their move / duplicate
 * / delete handle. Every site instruction must look like every other one.
 */
export const LOCKED_BLOCK_TYPES: ReadonlySet<BlockType> = new Set<BlockType>([
  'instruction_list',
  'signature',
  'drawing_list',
  'qa_list',
]);

// =======================================
// BLOCK DATA STRUCTURES
// =======================================

/**
 * Heading block data
 */
export interface HeadingBlockData {
  level: 1 | 2 | 3;
  text: string;
}

/**
 * Paragraph block data
 */
export interface ParagraphBlockData {
  content: string;
}

/**
 * Scope list item
 */
export interface ScopeListItem {
  id: string;
  text: string;
  indent: number;
  completed?: boolean;
}

/**
 * Scope list block data
 */
export interface ScopeListBlockData {
  items: ScopeListItem[];
  showCheckboxes?: boolean;
}

/** How a list block marks its items: "•", "1." or "a.". */
export type ListStyle = 'bullet' | 'number' | 'letter';

/**
 * List block data. One block type for bulleted, numbered and lettered lists,
 * so turning one into another changes only the markers. `ordered` predates
 * `style` and is kept in step with it (true for numbers and letters); a list
 * saved before `style` existed is read through listStyleOf.
 */
export interface BulletListBlockData {
  items: string[];
  ordered: boolean;
  style?: ListStyle;
}

export const listStyleOf = (data: BulletListBlockData): ListStyle =>
  data.style ?? (data.ordered ? 'number' : 'bullet');

/** The data for a list in `style`, with `ordered` kept in step. */
export const withListStyle = (data: BulletListBlockData, style: ListStyle): BulletListBlockData => ({
  ...data,
  style,
  ordered: style !== 'bullet',
});

/**
 * The marker for item `index`: what DocumentPDF prints, and what the editor
 * draws for numbers and letters (BulletListBlock's data-marker), so both
 * surfaces show the same text in the same place. Letters run a..z, then
 * aa, ab ... az, ba, as CSS lower-alpha does. Bullets are drawn by the
 * browser as a disc in the editor.
 */
export const listMarker = (style: ListStyle, index: number): string => {
  if (style === 'bullet') return '•';
  if (style === 'number') return `${index + 1}.`;
  let n = index + 1;
  let letters = '';
  while (n > 0) {
    n -= 1;
    letters = String.fromCharCode(97 + (n % 26)) + letters;
    n = Math.floor(n / 26);
  }
  return `${letters}.`;
};

/**
 * Table cell
 */
export interface TableCell {
  content: string;
  header?: boolean;
}

/**
 * Table block data
 */
export interface TableBlockData {
  rows: TableCell[][];
  hasHeaderRow: boolean;
  columnWidths?: number[]; // Percentage widths for each column (undefined = auto)
}

/**
 * Image block data
 */
export type ImageSize = 'small' | 'medium' | 'large' | 'full';
export type ImageAlignment = 'left' | 'center' | 'right';
export type ImageLayout = 'single' | 'two-column' | 'grid';
export type ImageAspect = 'horizontal-4-3' | 'horizontal-16-9' | 'vertical-3-4' | 'vertical-9-16' | 'square';

/**
 * Aspect used by photo appendices that have no explicit setting (new ones and
 * older documents saved before the setting existed). Site photos are mostly
 * taken in portrait, so vertical 3:4 is the default.
 */
export const DEFAULT_APPENDIX_ASPECT: ImageAspect = 'vertical-3-4';

export interface ImageItem {
  id: string;
  storagePath: string;
  caption?: string;
  alt?: string;
}

export interface ImageBlockData {
  imageId: string | null;
  storagePath: string | null;
  caption: string;
  alt: string;
  size?: ImageSize;
  alignment?: ImageAlignment;
  layout?: ImageLayout;
  aspect?: ImageAspect;
  images?: ImageItem[]; // For multi-image layouts
}

/**
 * Image appendix item
 */
export interface ImageAppendixItem {
  id: string;
  imageId: string | null;
  storagePath: string | null;
  caption: string;
  /**
   * The photo's own frame shape, fitted inside its page's frame so the whole
   * photo shows. Unset: the photo takes its page's aspect (and is cropped to
   * it). Set at upload when a photo is turned the other way to its page.
   */
  aspect?: ImageAspect;
}

/** How one page of a photo appendix is laid out. */
export interface PhotoPageLayout {
  columns: 1 | 2;
  rows: 1 | 2;
  aspect: ImageAspect;
}

/**
 * Image appendix block data. `columns`, `rows` and `aspect` are the layout of
 * every page that has none of its own.
 */
export interface ImageAppendixBlockData {
  title: string;
  columns: 1 | 2;
  rows: 1 | 2;
  images: ImageAppendixItem[];
  aspect?: ImageAspect;
  /**
   * Pages laid out differently from the appendix, keyed on the id of the
   * photo that starts the page. Keyed on a photo, not a page number, because
   * pages are derived from the photos: a page number would hand a landscape
   * layout to whichever photos slid onto that page. See photoAppendixPages.
   */
  pageLayouts?: Record<string, PhotoPageLayout>;
  // When true, photo numbering continues from the previous appendix block(s)
  // in the same document instead of restarting at 1.
  continueNumbering?: boolean;
}

/**
 * Divider block data
 */
export interface DividerBlockData {
  style: 'solid' | 'dashed' | 'dotted';
}

/**
 * Spacer block data
 */
export interface SpacerBlockData {
  height: 'small' | 'medium' | 'large';
}

/**
 * Signature block data
 * Note: The actual signatory is stored at document level (signatory_id)
 * This block just renders it and provides UI for changing it
 */
export interface SignatureBlockData {
  closing: string;
  company: string;
  /**
   * Distribution checkboxes, rendered as a column to the RIGHT of the signature.
   *
   * They live on the signature block rather than in a block of their own
   * because the printed form puts them on one line, and the editor measures a
   * block to decide where the page breaks: two adjacent blocks laid out side by
   * side would measure as the SUM of their heights when the line is only as
   * tall as the taller of the two. One line has to be one block.
   *
   * Absent on a letter, whose signature spans the full width.
   */
  distribution?: DistributionItem[];
}

/**
 * One numbered row of a site instruction. The REF ("1.0", "2.0", ...) is
 * derived from the row's position, never stored -- otherwise deleting row 2
 * leaves a gap that has to be repaired everywhere the block is rendered.
 */
export interface InstructionRow {
  id: string;
  /**
   * The item's content, as ordinary document blocks -- paragraphs, bullet
   * lists and headings. Using the same block types the report uses means the
   * editor components, the PDF renderers and the line measurement all work
   * inside an instruction without a second implementation of any of them.
   */
  blocks?: Block[];
  /**
   * @deprecated Single-line text, from before an item could hold blocks. Still
   * read (never written) so instructions written under the old shape keep
   * rendering. Read through instructionRowBlocks(), never directly.
   */
  text?: string;
}

/**
 * Which slice of a row-based block -- an instruction list, a drawing list --
 * one page shows.
 *
 * These blocks break BETWEEN rows: an instruction is never cut in half and a
 * drawing never straddles a page, so a fragment is a range of rows rather than
 * a range of lines the way a paragraph's is. `part` is 0-based; `parts` is
 * back-filled once the last fragment is placed, so a block that turned out to
 * fit on one page can have its fragment dropped entirely.
 */
export interface RowFragment {
  rowStart: number;
  rowEnd: number;
  part: number;
  parts: number;
}

/**
 * True when an item holds nothing worth confirming the loss of.
 *
 * Reads the text out of every block shape an item may contain, so a bullet list
 * or a heading counts as content just as a paragraph does. Paragraph content is
 * the contenteditable's innerHTML, hence the tag strip -- an "empty" paragraph
 * is often <br> or an empty <div> rather than "".
 */
export const instructionRowIsEmpty = (row: InstructionRow): boolean =>
  instructionRowBlocks(row).every(block => {
    switch (block.type) {
      case 'paragraph':
        return !((block.data as ParagraphBlockData).content || '')
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .trim();
      case 'heading':
        return !((block.data as HeadingBlockData).text || '').trim();
      case 'bullet_list':
        return ((block.data as BulletListBlockData).items || [])
          .every(item => !(item || '').trim());
      default:
        // An unexpected block type is content: better to ask than to discard.
        return false;
    }
  });

/** The block types an instruction item may contain. */
export const INSTRUCTION_BLOCK_TYPES: BlockType[] = ['paragraph', 'bullet_list', 'heading'];

/**
 * An item's blocks, whichever shape it was stored in.
 *
 * The synthesised id for a legacy row is derived from the row id rather than
 * generated, because it is used as a measurement key -- a fresh uuid on every
 * render would invalidate the measurement cache every pass and the paginate
 * loop would never settle.
 */
export const instructionRowBlocks = (row: InstructionRow): Block[] => {
  if (row.blocks && row.blocks.length > 0) return row.blocks;
  return [{
    id: `${row.id}-text`,
    type: 'paragraph',
    data: { content: row.text ?? '' } as ParagraphBlockData,
  }];
};

export interface InstructionListBlockData {
  rows: InstructionRow[];
}

/**
 * One drawing on a design certificate, as captured into the document.
 *
 * A copy of a project_drawings row, not a reference to it: an issued
 * certificate must say what it said when it was signed, even after the drawing
 * set is revised. `sourceId` is the row it came from, so a refresh can tell an
 * updated drawing from a new one.
 */
export interface CertifiedDrawing {
  id: string;
  sourceId: number | null;
  drawing_number: string;
  issue_number: string;
  title: string;
  /** Cited in the "Specific Design Criteria..." sentence above the list. */
  is_design_standard: boolean;
}

/**
 * An empty drawing, for one the author adds themselves.
 *
 * Blank on purpose, as the QA list's is: a seeded row reads as somebody else's
 * half-written entry. `sourceId` null marks it as the author's -- captureDrawings
 * keeps such rows through a refresh, because the drawing set has no counterpart
 * to restore them from.
 */
export const blankCertifiedDrawing = (): CertifiedDrawing => ({
  id: generateBlockId(),
  sourceId: null,
  drawing_number: '',
  issue_number: '',
  title: '',
  is_design_standard: false,
});

export interface DrawingListBlockData {
  rows: CertifiedDrawing[];
  /**
   * Whether the block draws its own lead-in -- the design-criteria sentence and
   * "The drawings relevant to this Certification are listed below:" -- above
   * the rows. Absent means yes: every certificate created before those two
   * sentences became ordinary editable paragraphs has them only here, and an
   * issued certificate must go on saying them. New certificates set false and
   * carry them as paragraphs instead (see createDesignCertificateBlocks).
   */
  showsLeadIn?: boolean;
  /**
   * The bare enum letter ('A', 'H1', ...) captured with the rows; the block
   * renders it as Class 'A'. Null when neither the stage nor the project has
   * one set, which the block shows as a placeholder rather than a gap.
   */
  site_classification: string | null;
}

/**
 * "5910-STD-005(A)" -- how a drawing is cited in the design criteria sentence.
 * Note the parentheses: the table below it writes the same value as "Issue A".
 */
export const drawingReference = (d: CertifiedDrawing): string =>
  `${d.drawing_number}(${d.issue_number})`;

/**
 * "X", "X and Y", "X, Y and Z" -- an English list, because this renders into a
 * sentence in a document that goes to a client.
 */
export const joinWithAnd = (parts: string[]): string =>
  parts.length <= 1
    ? parts[0] ?? ''
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/**
 * The certificate's design-criteria sentence, composed from the captured rows.
 * Singular for one drawing, plural for several.
 */
export const designCriteriaSentence = (data: DrawingListBlockData): string => {
  const standards = (data.rows || []).filter(r => r.is_design_standard);
  const drawings = standards.length
    ? `${standards.length === 1 ? 'drawing' : 'drawings'} ${joinWithAnd(standards.map(drawingReference))}`
    : 'the drawings marked as design standards';
  const siteClass = data.site_classification
    ? `Class '${data.site_classification}'`
    : '[site classification]';
  return `Specific Design Criteria are in accordance with those stated on ${drawings}. Suitable for the ${siteClass} site classification.`;
};

/**
 * One QA result an As Constructed Certificate relies on.
 *
 * Seeded from the job's ticked RWC QA items, then AUTHORED: the text is the
 * engineer's sentence, not the PEP's, and the reference under it is typed from
 * the lab's report. That is why a refresh reconciles by `qa_type` and keeps the
 * text of a row it already has, where the drawing list -- whose rows are facts
 * the drawing set owns -- takes the source's values back.
 */
export interface QaCertificationRow {
  id: string;
  /**
   * The `rwc_qa_type` this row was seeded from, and the key a refresh matches
   * on. Empty for a row the author added by hand, which no refresh ever drops.
   */
  qa_type: string;
  /** The bullet: "Block density and strength results provided by X". */
  text: string;
  /**
   * The dash line under it: the lab's job or sample number. Null when the
   * author has removed the line (Backspace in it while empty) because there is
   * no reference to give -- neither surface draws the dash then. An empty
   * string is different: the line is there, waiting to be typed into.
   */
  detail: string | null;
}

export interface QaListBlockData {
  rows: QaCertificationRow[];
}

/** A contact as the document surfaces embed it from PostgREST. */
export interface DocumentContact {
  first_name: string;
  last_name: string;
}

/**
 * The contact a document is addressed to (Attention / Dear): the stage's own
 * primary contact when the document is on a stage that has one, else the
 * project's. A stage's null means it inherits. One rule for the editor, the
 * export check and the PDF, so the three cannot drift apart.
 */
export const effectiveContact = (
  projectData: { contacts: DocumentContact | null; stage?: { contacts?: DocumentContact | null } | null } | null | undefined
): DocumentContact | null => projectData?.stage?.contacts ?? projectData?.contacts ?? null;

/**
 * What each RWC QA type reads as on the certificate, up to the provider.
 *
 * Taken verbatim from the certificates this template replaces -- note that
 * compaction is "carried out by" where the tests are "provided by", because the
 * contractor does it and a laboratory reports it. Seeded into the row's text
 * and editable from there, so a one-off can be reworded without a code change.
 *
 * Keyed on the enum value. A type with no entry here still gets a row, worded
 * with QA_STATEMENT_FALLBACK -- silently omitting a completed QA item from a
 * certification would be the worst possible failure of this block, so an
 * unrecognised type must never be a reason to drop one.
 */
export const QA_CERTIFICATION_WORDING: Record<string, string> = {
  block_density_crushing: 'Block density and strength results provided by',
  mortar_mix_composition: 'Mortar composition test results provided by',
  footing_backfill_compaction: 'Footing and backfill compaction results carried out by',
  concrete_strength_testing: 'Concrete strength test results provided by',
  concrete_delivery_docket: 'Concrete delivery dockets provided by',
};

/**
 * The wording for a QA type this file does not recognise -- a checklist item
 * whose database change shipped ahead of the frontend. A whole clause rather
 * than a bare prompt, so the bullet reads as a sentence with a gap in it.
 *
 * NOT used for a bullet the author adds by hand: that one starts blank, because
 * a prompt for a QA type nobody chose only invites the wrong question.
 */
const QA_STATEMENT_FALLBACK = 'Results provided by';

/** The text a row of this type starts life with, before the author edits it. */
export const seededQaText = (qaType: string): string =>
  `${QA_CERTIFICATION_WORDING[qaType] || QA_STATEMENT_FALLBACK} ${QA_PROVIDER_PLACEHOLDER}`;

/** Filled in by the author. Rendered as written -- they ARE the prompt. */
export const QA_PROVIDER_PLACEHOLDER = '[enter material consultant]';
export const QA_DETAIL_PLACEHOLDER = '[enter details]';
export const DRAWINGS_PLACEHOLDER = '[enter drawings]';
export const CONSULTANT_PLACEHOLDER = '[enter consultant]';
export const CLIENT_PLACEHOLDER = '[enter client]';
export const VISIT_COUNT_PLACEHOLDER = '[enter number]';

/** The bullet a completed QA item starts life as. */
export const qaRowForType = (qaType: string): QaCertificationRow => ({
  id: generateBlockId(),
  qa_type: qaType,
  text: seededQaText(qaType),
  detail: QA_DETAIL_PLACEHOLDER,
});

/**
 * An empty bullet, for one the author adds themselves.
 *
 * Blank on purpose. Seeding it with the fallback wording made two hand-added
 * rows read as identical half-finished sentences, which is worse than an empty
 * line waiting to be typed into. Both surfaces reserve the line, so an empty
 * row is a real two-line row and not a gap that moves the page break.
 */
export const blankQaRow = (): QaCertificationRow => ({
  id: generateBlockId(),
  qa_type: '',
  text: '',
  detail: '',
});

/**
 * True when nothing has been written into this bullet yet -- both lines are
 * still exactly what it was seeded with.
 *
 * Removing such a row loses nothing: a refresh puts it straight back if its QA
 * item is still completed, and a blank hand-added one held nothing to lose.
 * Removing an edited one is irreversible, because the refresh reseeds the
 * placeholder wording rather than restoring what was typed.
 */
export const qaRowIsUnedited = (row: QaCertificationRow): boolean =>
  (row.text === '' && !row.detail)
  || (row.text === seededQaText(row.qa_type) && row.detail === QA_DETAIL_PLACEHOLDER);

export interface DistributionItem {
  id: string;
  label: string;
  checked: boolean;
}

// =======================================
// APPENDIX BLOCKS: table, plan, attachment
// =======================================

/**
 * One column of a table appendix.
 *
 * `item` and `image` are DERIVED columns: the item number comes from the row's
 * position and the photo reference from the photos the row is linked to, so
 * neither can drift from the appendix they describe when rows or photos are
 * reordered. Every other column is free text the author owns -- which is what
 * lets the same block be an inspection record, a schedule of works or a bill of
 * quantities by adding columns. Every table has one of each derived column;
 * either may be HIDDEN (a simpler report's photo list needs no item numbers)
 * but never deleted -- it holds nothing of its own, and hidden it keeps its
 * header, width and place for when it is shown again. See columnRemovalRefusal.
 */
export type TableColumnKind = 'item' | 'image' | 'text';

export interface TableAppendixColumn {
  id: string;
  kind: TableColumnKind;
  label: string;
  /**
   * Share of the table width. The VISIBLE columns of a table sum to 100; a
   * hidden one keeps its share for when it is shown again.
   */
  widthPct: number;
  /** Item or Photo only: left off the page and the PDF. */
  hidden?: boolean;
}

/** A photo as the table's photo picker lists it. */
export interface TablePhotoOption {
  /** The ImageAppendixItem id. */
  id: string;
  number: number;
  caption: string;
  /** A signed URL for its thumbnail, once fetched. */
  url: string | null;
}

/** The columns drawn on the page and in the PDF. */
export const visibleTableColumns = (columns: TableAppendixColumn[]): TableAppendixColumn[] =>
  columns.filter(c => !c.hidden);

/**
 * One row of a table appendix. A `section` row is a heading spanning every
 * column (e.g. "STAFF ACCOMMODATION"); it carries no cells and takes no item
 * number.
 *
 * `imageIds` are the ids of ImageAppendixItems this row is about. The Image
 * column is derived from them while the list is non-empty; a row with none
 * shows whatever was typed into that cell instead, so a table can also be
 * written without photos at all.
 */
export interface TableAppendixRow {
  id: string;
  kind: 'row' | 'section';
  imageIds: string[];
  /** Typed text keyed on column id. Absent keys read as empty. */
  cells: Record<string, string>;
  /** The heading text, for a section row. */
  heading?: string;
}

export interface TableAppendixBlockData {
  title: string;
  columns: TableAppendixColumn[];
  rows: TableAppendixRow[];
}

/**
 * A numbered marker on a location plan. `x` / `y` are fractions of the FITTED
 * plan image box (0..1), not pixels, so the same numbers place it on both
 * surfaces whatever the box resolves to; `rotation` is degrees clockwise from
 * "arrow pointing up".
 *
 * It labels itself from what it is linked to -- a table row or a photo -- via
 * the plan's `labelSource`, so renumbering the table or the photos renumbers
 * the plan with them. See markerLabel() in utils/planMarkers.ts.
 */
export interface PlanMarker {
  id: string;
  x: number;
  y: number;
  rotation: number;
  rowId?: string | null;
  imageId?: string | null;
}

export type PlanAlign = 'left' | 'center' | 'right';
export type PlanVAlign = 'top' | 'middle';

/**
 * One page of a location plan appendix: one plan (a level, a wing, a site)
 * with its own pins. A building with three levels is one appendix of three
 * pages, and a photo placed on any of them counts as placed for the appendix
 * -- the pool of photos is shared, the pages are where they land.
 */
export interface PlanPage {
  id: string;
  /** The line above the plan. The first page's is the appendix title. */
  title?: string;
  storagePath: string | null;
  /** Pixel size of the uploaded bitmap; the fitted box is derived from it. */
  naturalWidth: number;
  naturalHeight: number;
  markers: PlanMarker[];
  /**
   * Where the fitted plan sits in the page box when it does not fill it. A
   * plan is fitted within its own aspect ratio, so a wide plan leaves room
   * below and a tall one room beside; these place it. Absent means centred
   * horizontally and at the top, under the title.
   */
  align?: PlanAlign;
  valign?: PlanVAlign;
}

export interface PlanAppendixBlockData {
  title: string;
  /** What a marker's number means: a photo's number or a table row's item. */
  labelSource: 'image' | 'item';
  /** The table appendix rows are taken from, when labelSource is 'item'. */
  tableBlockId?: string | null;
  /** The plan pages, in order. Read through planPages(): older blocks hold one plan in the fields below instead. */
  pages?: PlanPage[];
  /** @deprecated Single-plan shape, from before an appendix could have several pages. Read via planPages(), never written. */
  storagePath?: string | null;
  /** @deprecated See storagePath. */
  naturalWidth?: number;
  /** @deprecated See storagePath. */
  naturalHeight?: number;
  /** @deprecated See storagePath. */
  markers?: PlanMarker[];
  /** @deprecated See storagePath. */
  align?: PlanAlign;
  /** @deprecated See storagePath. */
  valign?: PlanVAlign;
}

export const blankPlanPage = (): PlanPage => ({
  id: generateBlockId(),
  storagePath: null,
  naturalWidth: 0,
  naturalHeight: 0,
  markers: [],
});

/**
 * A plan appendix's pages, whichever shape it was stored in. A block written
 * before pages existed holds one plan in the top-level fields; it is read as
 * a single page with a fixed id (derived, not generated -- the id is a React
 * key and a measurement key, and a fresh one each read would remount it).
 */
export const planPages = (data: PlanAppendixBlockData): PlanPage[] => {
  if (data.pages && data.pages.length > 0) return data.pages;
  return [{
    id: 'legacy-plan',
    storagePath: data.storagePath ?? null,
    naturalWidth: data.naturalWidth ?? 0,
    naturalHeight: data.naturalHeight ?? 0,
    markers: data.markers ?? [],
    align: data.align,
    valign: data.valign,
  }];
};

/**
 * The block data with `pages` canonical and the single-plan fields gone.
 * Every write to a plan appendix starts from this, so the old shape is
 * converted the first time the block is touched and never written again.
 */
export const normalisePlan = (data: PlanAppendixBlockData): PlanAppendixBlockData => {
  const { storagePath, naturalWidth, naturalHeight, markers, align, valign, ...rest } = data;
  void storagePath; void naturalWidth; void naturalHeight; void markers; void align; void valign;
  return { ...rest, pages: planPages(data) };
};

/** Every marker on every page of a plan appendix. */
export const planMarkersAll = (data: PlanAppendixBlockData): PlanMarker[] =>
  planPages(data).flatMap(p => p.markers);

/**
 * One page of an attached PDF, as recorded at upload. The size is the page's
 * own (an A3 landscape sheet stays A3 landscape in the export), and the
 * thumbnail is a JPEG rasterised once at upload so the editor never has to
 * run a PDF renderer to show it.
 */
export interface AttachmentPage {
  widthPt: number;
  heightPt: number;
  thumbPath: string;
}

export interface PdfAttachmentBlockData {
  title: string;
  storagePath: string | null;
  filename: string;
  pages: AttachmentPage[];
}

/**
 * A blank page. The block is a MARKER in the body flow that draws nothing:
 * the paginator starts a fresh page at it and labels that page and the body
 * pages after it (up to the next page-owning block) as the blank page's. What
 * goes on it is whatever ordinary blocks follow -- paragraphs, headings,
 * lists -- so it is measured, split between lines and replayed into the PDF
 * exactly as the letter is, with no second implementation of any of it.
 */
export interface BlankPageBlockData {
  /** Nothing yet; the marker has no content of its own. */
  _?: never;
}

/** The word a photo is labelled with in the photo appendix: "Photo 3". */
export const PHOTO_LABEL = 'Photo';

/**
 * A new derived column. The photo column is headed "Photo", the word the photo
 * appendix labels its photos with, so "Photo 3" reads the same in both places.
 * Tables made before this say "Image" and keep it: a header is the author's
 * text, and rewriting it would change documents already issued.
 */
const derivedTableColumn = (kind: 'item' | 'image'): TableAppendixColumn => (
  kind === 'item'
    ? { id: generateBlockId(), kind, label: 'Item', widthPct: 12 }
    : { id: generateBlockId(), kind, label: PHOTO_LABEL, widthPct: 14 }
);

/** Default column set of a new table appendix -- the inspection record. */
const createDefaultTableColumns = (): TableAppendixColumn[] => [
  derivedTableColumn('item'),
  derivedTableColumn('image'),
  { id: generateBlockId(), kind: 'text', label: 'Details', widthPct: 74 },
];

/**
 * Why a column may not be taken off the page -- a typed column removed, Item
 * or Photo hidden -- or null when it may:
 *  - the page keeps a column a section heading's words can go into when it is
 *    turned back into a row (headingColumn): any visible column but Item;
 *  - Item stays shown while a location plan numbers its markers from it: the
 *    pins would show numbers printed nowhere in the table.
 * Hiding Photo keeps the rows' photo links, so showing it brings the numbers
 * back.
 */
export const columnRemovalRefusal = (
  blocks: Block[],
  tableBlockId: string,
  columns: TableAppendixColumn[],
  column: TableAppendixColumn
): string | null => {
  const rest = visibleTableColumns(columns).filter(c => c.id !== column.id);
  if (!rest.some(c => c.kind !== 'item')) {
    return 'A table keeps at least one column that can be typed in';
  }
  if (column.kind === 'item' && tableNumbersAPlan(blocks, tableBlockId)) {
    return 'A location plan numbers its markers from this column';
  }
  return null;
};

/** Whether any location plan numbers its markers from this table's items. */
const tableNumbersAPlan = (blocks: Block[], tableBlockId: string): boolean =>
  blocks.some(b =>
    b.type === 'plan_appendix'
    && (b.data as PlanAppendixBlockData).labelSource === 'item'
    && (b.data as PlanAppendixBlockData).tableBlockId === tableBlockId);

/** No column narrower than this share of the table. */
export const MIN_COLUMN_WIDTH_PCT = 5;

/**
 * Rescale the columns so their widths sum to exactly 100, holding `keepId`'s
 * width where it is (the one just added or just typed) and sharing the rest
 * among the others in proportion to what they had, none below
 * MIN_COLUMN_WIDTH_PCT. Widths are whole percentages and the sum is exact, so
 * the editor's flex-basis and the PDF's width agree to the point.
 *
 * The floor is applied BEFORE rounding (a column whose share falls under it is
 * fixed at it and the rest re-shared), and the rounding is largest-remainder.
 * Flooring after the fact -- clamping whichever column came out short -- adds
 * width the others never gave up: [74, 12, 14] with the last set to 85 came
 * out at 103%, a row wider than the page in the PDF.
 *
 * Every edit of the column set goes through this, so the author never has to
 * balance the numbers by hand.
 */
export const normaliseColumnWidths = (
  allColumns: TableAppendixColumn[],
  keepId?: string
): TableAppendixColumn[] => {
  // Only the visible columns share the width; a hidden one keeps its own
  // share untouched, for when it is shown again.
  const columns = visibleTableColumns(allColumns);
  if (columns.length === 0) return allColumns;
  const kept = columns.find(c => c.id === keepId);
  const others = columns.filter(c => c !== kept);
  const keptWidth = !kept
    ? 0
    : others.length === 0
      ? 100
      : Math.min(
          100 - MIN_COLUMN_WIDTH_PCT * others.length,
          Math.max(MIN_COLUMN_WIDTH_PCT, Math.round(kept.widthPct) || MIN_COLUMN_WIDTH_PCT)
        );
  const remaining = 100 - keptWidth;

  // Water-fill: any column whose proportional share is under the floor is
  // fixed at the floor, and the rest is re-shared among the others.
  const ideal = new Map<string, number>();
  let pool = others;
  let budget = remaining;
  if (budget >= MIN_COLUMN_WIDTH_PCT * others.length) {
    for (;;) {
      let total = 0;
      for (const c of pool) total += Math.max(0, c.widthPct || 0);
      const shares = new Map<string, number>();
      for (const c of pool) {
        shares.set(c.id, total > 0 ? (budget * Math.max(0, c.widthPct || 0)) / total : budget / pool.length);
      }
      const low = new Set<string>();
      shares.forEach((share, id) => { if (share < MIN_COLUMN_WIDTH_PCT) low.add(id); });
      if (low.size === 0 || low.size === pool.length) {
        shares.forEach((share, id) => ideal.set(id, Math.max(MIN_COLUMN_WIDTH_PCT, share)));
        break;
      }
      low.forEach(id => ideal.set(id, MIN_COLUMN_WIDTH_PCT));
      budget -= MIN_COLUMN_WIDTH_PCT * low.size;
      const remainingPool: TableAppendixColumn[] = [];
      for (const c of pool) if (!low.has(c.id)) remainingPool.push(c);
      pool = remainingPool;
    }
  } else {
    // More columns than the floor allows (over 20): share equally.
    others.forEach(c => ideal.set(c.id, remaining / others.length));
  }

  // Largest remainder: floor every share, then hand the leftover points to the
  // largest fractions, so the whole-number widths sum to exactly `remaining`.
  const scaled = new Map<string, number>();
  others.forEach(c => scaled.set(c.id, Math.floor(ideal.get(c.id) ?? 0)));
  let leftover = remaining - others.reduce((n, c) => n + (scaled.get(c.id) ?? 0), 0);
  const byFraction = [...others].sort((a, b) =>
    ((ideal.get(b.id) ?? 0) % 1) - ((ideal.get(a.id) ?? 0) % 1));
  for (let i = 0; leftover > 0 && byFraction.length > 0; i = (i + 1) % byFraction.length, leftover--) {
    const id = byFraction[i].id;
    scaled.set(id, (scaled.get(id) ?? 0) + 1);
  }

  return allColumns.map(c => (c.hidden ? c : {
    ...c,
    widthPct: c === kept ? keptWidth : (scaled.get(c.id) ?? c.widthPct),
  }));
};

/**
 * Link a photo to a row, or unlink it. One photo, one item: linking a photo
 * another row has moves it here, and that row goes too when the photo was
 * all it had -- no text, no other photo, not on a location plan
 * (`isPlaced`). That makes grouping the one-row-per-photo table "Fill from
 * photos" builds a single step: tick Photo 4 on item 3 and item 4 is gone.
 * Unlinking never removes the row being edited.
 */
export const setRowPhoto = (
  rows: TableAppendixRow[],
  rowId: string,
  imageId: string,
  linked: boolean,
  columns: TableAppendixColumn[],
  isPlaced: (rowId: string) => boolean
): TableAppendixRow[] => {
  if (!linked) {
    return rows.map(r => (r.id === rowId ? { ...r, imageIds: r.imageIds.filter(id => id !== imageId) } : r));
  }
  return rows.flatMap(r => {
    if (r.id === rowId) {
      return [r.imageIds.includes(imageId) ? r : { ...r, imageIds: [...r.imageIds, imageId] }];
    }
    if (!r.imageIds.includes(imageId)) return [r];
    const left = { ...r, imageIds: r.imageIds.filter(id => id !== imageId) };
    const empty = left.kind === 'row' && !tableRowHasContent(left, columns) && !isPlaced(r.id);
    return empty ? [] : [left];
  });
};

export const blankTableRow = (): TableAppendixRow => ({
  id: generateBlockId(),
  kind: 'row',
  imageIds: [],
  cells: {},
});

export const blankTableSection = (): TableAppendixRow => ({
  id: generateBlockId(),
  kind: 'section',
  imageIds: [],
  cells: {},
  heading: '',
});

/**
 * Whether a row has anything in it: typed text in any column the table still
 * has, or a linked photo. Only an EMPTY row may be turned into a section
 * heading -- a heading shows no cells, so converting a row with content would
 * hide it (or, if dropped, lose it). The row actions also refuse a row that is
 * placed on a location plan (isTableRowOnAPlan), which a heading cannot be.
 */
export const tableRowHasContent = (row: TableAppendixRow, columns: TableAppendixColumn[]): boolean =>
  row.imageIds.length > 0
  || columns.some(c => c.kind !== 'item' && (row.cells[c.id] || '').trim() !== '');

/**
 * The column a heading's words go into when it is turned back into a row:
 * the table's first text column, or failing that its first typed one (an
 * Image column types like text while the row links no photo, which a heading
 * never does), so the words always have somewhere to go.
 */
export const headingColumn = (columns: TableAppendixColumn[]): TableAppendixColumn | undefined =>
  columns.find(c => c.kind === 'text') ?? columns.find(c => c.kind !== 'item');

/**
 * Turn an empty row into a section heading, or a heading into a row, in
 * place. The caller checks the row is empty first (tableRowHasContent); a
 * heading always converts, its text moving into headingColumn so nothing is
 * lost either way.
 */
export const toggleTableRowKind = (
  row: TableAppendixRow,
  columns: TableAppendixColumn[]
): TableAppendixRow => {
  if (row.kind === 'row') return { ...row, kind: 'section', heading: '' };
  const { heading = '', ...rest } = row;
  const target = headingColumn(columns);
  return {
    ...rest,
    kind: 'row',
    cells: target && heading ? { ...row.cells, [target.id]: heading } : row.cells,
  };
};

/**
 * Every photo's number, keyed on the ImageAppendixItem id, across every photo
 * appendix in the document -- honouring each appendix's continueNumbering the
 * way the appendix pages themselves do (getAppendixStartNumber).
 *
 * Both the table's Image column and the plan's markers read from this, so a
 * photo moved between appendices or reordered within one renumbers everywhere
 * at once.
 */
export const photoNumbers = (blocks: Block[]): Map<string, number> => {
  const numbers = new Map<string, number>();
  for (const block of blocks) {
    if (block.type !== 'image_appendix') continue;
    const start = getAppendixStartNumber(blocks, block.id);
    ((block.data as ImageAppendixBlockData).images || []).forEach((img, i) => {
      numbers.set(img.id, start + i);
    });
  }
  return numbers;
};

/**
 * "7-8" for a consecutive run, "1, 3" otherwise, "1-3, 7" for a mix -- the way
 * the firm's inspection records have always cited a row's photos.
 */
export const formatImageRefs = (numbers: number[]): string => {
  const sorted = Array.from(new Set(numbers)).sort((a, b) => a - b);
  const parts: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    parts.push(j > i ? `${sorted[i]}-${sorted[j]}` : String(sorted[i]));
    i = j + 1;
  }
  return parts.join(', ');
};

/**
 * The item number of each row by position -- section rows take none and do not
 * break the count, so a table with headings still reads 1, 2, 3 down the page.
 * Null for a section.
 */
export const itemNumbers = (rows: TableAppendixRow[]): Array<number | null> => {
  let n = 0;
  return rows.map(row => (row.kind === 'row' ? ++n : null));
};

/**
 * What a row's Image cell shows: the numbers of its linked photos while it has
 * any, otherwise whatever was typed. A linked photo that no longer exists in
 * any appendix contributes nothing rather than a stale number.
 */
export const tableImageCell = (
  row: TableAppendixRow,
  column: TableAppendixColumn,
  numbers: Map<string, number>
): string => {
  const linked = row.imageIds
    .map(id => numbers.get(id))
    .filter((n): n is number => n != null);
  if (row.imageIds.length > 0) return formatImageRefs(linked);
  return row.cells[column.id] || '';
};

/**
 * Add a row for every photo the table does not yet have one for.
 *
 * A RECONCILE, not a re-capture: rows already present keep every word typed
 * into them, and a row whose photo has since been removed is left alone too
 * (the author decides whether the observation stands without its picture).
 * Only membership grows -- one new row per unlinked photo, each put where its
 * photo falls (photoRowInsertIndex) rather than at the end: a row deleted by
 * mistake and filled back in returns to its place. Returns how many were
 * added so the caller can say so.
 */
export const fillTableFromPhotos = (
  blocks: Block[],
  tableBlockId: string
): { blocks: Block[]; added: number } => {
  const table = blocks.find(b => b.id === tableBlockId && b.type === 'table_appendix');
  if (!table) return { blocks, added: 0 };
  const data = table.data as TableAppendixBlockData;
  const linked = new Set(data.rows.flatMap(r => r.imageIds));

  const numbers = photoNumbers(blocks);
  const rows = [...data.rows];
  let added = 0;
  // In photo order, each into the rows as they stand after the last, so a run
  // of missing photos goes in together and in order.
  for (const [imageId, n] of Array.from(numbers.entries()).sort((a, b) => a[1] - b[1])) {
    if (linked.has(imageId)) continue;
    rows.splice(photoRowInsertIndex(rows, n, numbers), 0, { ...blankTableRow(), imageIds: [imageId] });
    added++;
  }
  if (added === 0) return { blocks, added: 0 };

  return {
    blocks: blocks.map(b => (b.id === tableBlockId ? { ...b, data: { ...data, rows } } : b)),
    added,
  };
};

/**
 * Where the row for photo `n` goes: after the last row about an earlier photo,
 * and after any plain rows that follow it (notes written under that photo),
 * but never past a section heading -- the next heading starts another part of
 * the building. With no earlier photo in the table it goes before the first
 * row about a later one; with no photo rows at all, at the end.
 */
export const photoRowInsertIndex = (
  rows: TableAppendixRow[],
  n: number,
  numbers: Map<string, number>
): number => {
  const firstPhoto = (row: TableAppendixRow): number | null => {
    const linked = row.imageIds.map(id => numbers.get(id)).filter((x): x is number => x != null);
    return linked.length > 0 ? Math.min(...linked) : null;
  };
  let anchor = -1;
  rows.forEach((row, i) => {
    const first = firstPhoto(row);
    if (first != null && first < n) anchor = i;
  });
  if (anchor === -1) {
    const later = rows.findIndex(row => {
      const first = firstPhoto(row);
      return first != null && first > n;
    });
    return later === -1 ? rows.length : later;
  }
  let i = anchor + 1;
  while (i < rows.length && rows[i].kind === 'row' && firstPhoto(rows[i]) == null) i++;
  return i;
};

/**
 * Editing-only affordances on the new appendix pages carry these classes so
 * the Pages rail's thumbnail clones can strip them (the clone is a static copy
 * of the live page).
 */
export const APPENDIX_EDIT_ONLY_CLASSES = [
  '.table-appendix__row-actions',
  '.table-appendix__add-row',
  '.plan-appendix__handle',
  '.plan-appendix__toolbar',
  '.attachment-page__toolbar',
  '.blank-page-chip',
  '.page-layout-chip',
  '.photo-aspect-control',
];

/**
 * Lines from the top of the signature section down to the BOTTOM of the
 * signature space: the closing line (when there is one), the company line, and
 * the two-line space itself.
 *
 * The site instruction is the first document with no closing, and an empty
 * closing is NOT free: the browser collapses an empty div to zero height while
 * react-pdf still draws a full line for an empty <Text>. Both surfaces skip the
 * line together via this helper, or the stamp sits one line low in the PDF and
 * the block measures shorter in the editor than it draws.
 */
export const signatureSpaceLines = (closing: string): number => (closing ? 4 : 3);

/**
 * Signature placement geometry, shared by every surface that draws a signature.
 *
 * The base render height is the block's two-blank-line signature space:
 * 11pt x 1.15 line height x 2 = 25.3pt. A signer's `signature_scale`
 * multiplies it and their mm offsets shift the image from the space's
 * bottom-left, so the same three numbers place the ink identically in
 * `SignatureBlock`, `DocumentPDF` (as `LINE_PT * 2`), the Employee Management
 * placement mock and the signing dialog's preview.
 *
 * It lives here because it was previously copied into each of those files. A
 * placement is only meaningful against the type size it was chosen on: the
 * signing dialog drew the image at a flat 18mm in the app's own font, which
 * showed every signature at roughly double size and never overlapping the
 * rule -- not what signing would produce. A surface that hardcodes its own
 * number cannot be caught by reading any one file.
 */
export const SIGNATURE_BASE_HEIGHT_PT = 11 * 1.15 * 2;

/** Width of the printed signature rule (the letter's row of underscores). */
export const SIGNATURE_LINE_MM = 62;

/**
 * Union type for all block data
 */
export type BlockData = 
  | HeadingBlockData
  | ParagraphBlockData
  | ScopeListBlockData
  | BulletListBlockData
  | TableBlockData
  | ImageBlockData
  | ImageAppendixBlockData
  | SignatureBlockData
  | DividerBlockData
  | SpacerBlockData
  | InstructionListBlockData
  | DrawingListBlockData
  | QaListBlockData
  | TableAppendixBlockData
  | PlanAppendixBlockData
  | PdfAttachmentBlockData
  | BlankPageBlockData;


// =======================================
// DOCUMENT LIST ROWS
// =======================================
// Shared by every surface that lists documents for a job: the project/stage
// Documents tab and the builder's left-hand panel. One copy, so a row reads the
// same wherever it appears.

// A document row as returned by get_project_documents (a projection of
// document_list_view).
/**
 * A template the current user may create against a particular job, as returned
 * by get_available_document_templates. Narrower than DocumentTemplate: the RPC
 * returns only what a create flow needs, and it is the RPC -- not the client --
 * that decides which templates a job may have.
 */
export interface AvailableTemplate {
  id: string;
  code: string | null;
  name: string;
  document_type: string;
  category: string;
  display_order: number;
  numbering_pattern: string;
  signature_slots: SignatureSlot[];
  chrome: DocumentChromeVariant;
  creates_inspection: boolean;
}

export interface TabDocument {
  id: string;
  document_number: string;
  title: string | null;
  status: DocumentStatus;
  category: string;
  display_order: number;
  template_id: string;
  template_name: string;
  document_type: string;
  signature_slots: SignatureSlot[];
  signatures: DocumentSignature[];
  document_date: string;
  /**
   * Set for a template numbered with {sequence} -- today, site instructions.
   * It is what tells the row whether its number carries information the title
   * does not.
   */
  sequence_number: number | null;
  /**
   * True when issuing this document records a site inspection -- today, a site
   * instruction. It is what tells a list whether the document belongs with the
   * job's inspection record rather than with its reports and certificates,
   * without matching on the category's display string.
   */
  creates_inspection: boolean;
  /** Nominated to sign. Set from creation, so a Draft has one too. */
  signatory: { id: string; first_name: string; last_name: string } | null;
  /** Who raised it -- the fallback when nobody is nominated yet. */
  creator: { id: string; first_name: string; last_name: string } | null;
}

export const statusLabel: Record<DocumentStatus, string> = {
  Draft: 'Draft',
  'Awaiting Approval': 'Awaiting Approval',
  Signed: 'Signed',
  Sent: 'Sent',
};

export const statusClass: Record<DocumentStatus, string> = {
  Draft: 'is-draft',
  'Awaiting Approval': 'is-awaiting',
  Signed: 'is-signed',
  Sent: 'is-sent',
};

/**
 * The status colours in StatusPill's vocabulary, shared by the register, the
 * project modal's Documents tab and the editor so a status reads the same
 * everywhere. Signed is the success and Sent the step after it, matching an
 * invoice's Sent -> Entered.
 */
export const statusTone: Record<DocumentStatus, PillTone> = {
  Draft: 'warning',
  'Awaiting Approval': 'info',
  Signed: 'success',
  Sent: 'complete',
};

/**
 * Whose document this is: the nominated signatory, or whoever raised it if
 * nobody is nominated yet.
 *
 * Not read from the signatures: a Draft has none, and sign_document() requires
 * the signer to BE the signatory, so for a signed document the two are the same
 * person anyway.
 */
export const documentAuthor = (doc: TabDocument) => doc.signatory || doc.creator;

/**
 * One line, not a title over a subtitle.
 *
 * A document drawn from a numbered sequence leads with its number, because that
 * is the thing people refer to -- "SI2 - Structural Steel". Everything else
 * leads with its title: a report's number is the job number, which the project
 * modal you are standing in has already told you.
 */
export const documentLabel = (doc: TabDocument): string =>
  doc.sequence_number != null
    ? doc.document_number
    : doc.title || doc.document_number;

export const initials = (person: { first_name: string; last_name: string } | null): string =>
  person ? `${person.first_name?.[0] ?? ''}${person.last_name?.[0] ?? ''}`.toUpperCase() || '?' : '?';

export const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Blank-line spacing around a block. Each side is one blank line when on.
 * Adjacent blocks share their spacing (a below + an above make ONE blank
 * line, like CSS margin collapsing), and the first block on a page has no
 * space above it. Absent means both on (the default).
 */
export interface BlockSpacing {
  above: boolean;
  below: boolean;
}

export const DEFAULT_BLOCK_SPACING: BlockSpacing = { above: true, below: true };

export const getBlockSpacing = (block: Pick<Block, 'spacing'>): BlockSpacing => ({
  above: block.spacing?.above ?? DEFAULT_BLOCK_SPACING.above,
  below: block.spacing?.below ?? DEFAULT_BLOCK_SPACING.below,
});

/**
 * One piece of a paragraph that continues on the next page. Derived by the
 * pagination from measured line positions and never saved: the paragraph
 * stays a single block. Offsets are character offsets into the paragraph's
 * text; `sourceHtml` is the content those offsets were measured against.
 */
export interface ParagraphFragment {
  part: number;      // 0-based index of this piece
  parts: number;     // total pieces of the paragraph
  lineStart: number; // first line (inclusive) of the paragraph on this page
  lineEnd: number;   // last line (exclusive)
  charStart: number;
  charEnd: number;
  sourceHtml: string;
}

/**
 * Generic block structure
 */
export interface Block {
  id: string;
  type: BlockType;
  data: BlockData;
  spacing?: BlockSpacing;
}

// =======================================
// DOCUMENT TYPES
// =======================================

/**
 * Document lifecycle. Mirrors the document_status enum in Postgres.
 *
 * A document is editable only while Draft. Signing moves it forward: once every
 * required signature slot is filled it becomes Signed, and the database refuses
 * further content edits (enforce_document_edit_lock). Sent records that it has
 * gone to the client; it is still locked, and only a manager can reopen it.
 */
export type DocumentStatus = 'Draft' | 'Awaiting Approval' | 'Signed' | 'Sent';

/** Every required signature is in: Signed, or Signed and since Sent. */
export const isSignedStatus = (status: DocumentStatus | undefined | null): boolean =>
  status === 'Signed' || status === 'Sent';

/**
 * A signature slot declared by a template. `signer` says who is allowed to sign
 * it; the order of the array is the order the slots are presented in.
 */
export interface SignatureSlot {
  key: string;
  label: string;
  // author   = the nominated signatory (any engineer), signable only by them
  // certified = the signatory must be a CPEng engineer; signable by them or,
  //             on their behalf, by any manager (who stamps the engineer's own
  //             signature)
  // manager  = a manager's own approval signature
  signer: 'author' | 'certified' | 'manager';
  required?: boolean;
  disallow_self_approval?: boolean;
}

/**
 * The resolved signature to stamp into a signature block -- the image plus the
 * snapshotted identity of the person who signed the document's author slot.
 * `imageUrl` is a signed URL in the editor and a base64 data URL in the PDF.
 */
export interface SignatureStamp {
  imageUrl?: string | null;
  name: string;
  title?: string | null;
  registration?: string | null;
  // Placement snapshot (see team_members.signature_scale): scale multiplies the
  // base render height (the two-blank-line signature space), the offsets shift
  // the image right/down in mm from the bottom-left of that space so it can
  // overlap the rule and name like a wet signature.
  scale?: number;
  offsetXMm?: number;
  offsetYMm?: number;
  // width / height of the image file. react-pdf cannot size an absolutely
  // positioned image by height alone, so the PDF path measures the downloaded
  // image and computes the width from this. The editor lets CSS do it.
  aspectRatio?: number | null;
}

/**
 * A signature that has actually been given. Every signer detail is a snapshot
 * taken at signing time, not a live join -- an issued document must keep
 * rendering the title and registration that were true when it was signed.
 */
export interface DocumentSignature {
  slot_key: string;
  slot_label: string;
  signed_by: string;
  /** Set when a manager signed on behalf of a certified engineer. */
  on_behalf_of?: string | null;
  signed_at: string;
  signer_name: string;
  signer_position_title: string | null;
  signer_registration_number: string | null;
  signature_storage_path?: string | null;
  /** Placement snapshot taken at signing time; see SignatureStamp. */
  signature_scale?: number;
  signature_offset_x_mm?: number;
  signature_offset_y_mm?: number;
}

/**
 * Document template structure
 */
export interface DocumentTemplate {
  id: string;
  /** Stable natural key used by reference-data migrations. */
  code: string | null;
  name: string;
  document_type: string;
  /** Grouping heading in the project/stage Documents tab. */
  category: string;
  display_order: number;
  /** Empty array means the template is offered for every project type. */
  applies_to_job_types: string[];
  signature_slots: SignatureSlot[];
  /**
   * How the page is DRAWN. Deliberately separate from document_type, which is
   * free text and only says what the template is called: a future template may
   * want an existing chrome under a new name.
   */
  chrome: DocumentChromeVariant;
  /** True when issuing a document of this template records a site inspection. */
  creates_inspection: boolean;
  description: string | null;
  blocks: Block[];
  numbering_pattern: string;
  statuses: string[];
  can_create: string[];
  can_view: string[];
  show_in_project_detail: boolean;
  show_in_stage_detail: boolean;
  show_in_invoice_register: boolean;
  pdf_settings: PDFSettings;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * How a document's page-1 chrome is drawn.
 *
 * Each variant has a matching renderer in DocumentChrome (editor) and
 * DocumentPDF (export). Adding a value means adding both.
 */
export type DocumentChromeVariant = 'letter' | 'site_instruction';

/**
 * PDF settings for document templates
 */
export interface PDFSettings {
  page_size: 'A4' | 'Letter';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

/**
 * Header fields for report documents (user-entered per document)
 */
export interface ReportHeaderFields {
  document_date: string;     // The document date (defaults to creation date, editable)
  contact_name: string;
  dear_name: string;
  // Client address - structured for Google Maps integration
  client_street: string;     // Street number + street name
  client_city: string;       // City/Suburb
  client_state: string;      // State (e.g., NSW, VIC)
  client_postcode: string;   // Postcode
  // Legacy field - kept for backward compatibility with existing documents
  client_address?: string;
  project_address: string;
  report_title: string;
}

/**
 * Header fields for site instruction documents.
 *
 * Every key is an OVERRIDE: blank means "derive live from the project/stage",
 * exactly like the report's Overrides. The values are snapshotted into this
 * blob when the document is issued, so a signed SI's PDF never changes if the
 * project is edited afterwards.
 *
 * The SI number is NOT here -- it is documents.sequence_number, because the
 * next auto-allocation is MAX(sequence_number) + 1 and because the two-second
 * autosave would clobber a value living in this blob.
 */
export interface SiteInstructionHeaderFields {
  document_date: string;
  project: string;
  project_no: string;
  client_architect: string;
  contractor: string;
  /** How the instruction was arrived at. One of SI_METHOD_OPTIONS. */
  method: string;
}

/**
 * How a site instruction was arrived at.
 *
 * Written explicitly into header_fields at creation rather than left blank and
 * defaulted at render time: an issued instruction has to keep saying what it
 * said, and a blank that renders as today's default would silently re-read if
 * the default ever changed.
 */
export const SI_METHOD_OPTIONS = ['On Site', 'Photographic Record'] as const;
export const SI_METHOD_DEFAULT: string = SI_METHOD_OPTIONS[0];

/**
 * header_fields as stored on a document.
 *
 * A union of every template's shape, all-optional, so a component that knows
 * its chrome reads its own keys without a cast and every existing Standard
 * Report row already satisfies the type. document_date is the one key every
 * template writes.
 */
export type DocumentHeaderFields =
  Partial<ReportHeaderFields> & Partial<SiteInstructionHeaderFields> & { document_date: string };

/**
 * One editable override in the editor's Fields sidebar.
 *
 * Keyed on CHROME rather than on the template's code: the chrome decides what
 * the page draws, so it decides which values there are to override. A second
 * template reusing an existing chrome gets the right fields for free.
 */
export interface HeaderFieldDescriptor {
  /** Which header_fields key this writes. Omitted for bindsSequence/bindsTitle. */
  key?: keyof DocumentHeaderFields;
  label: string;
  type: 'text' | 'date' | 'select';
  /** The choices, for type 'select'. */
  options?: readonly string[];
  /** Literal placeholder, for a field with no live project value behind it. */
  placeholder?: string;
  /** Which live project value to show as the placeholder when blank. */
  derivedFrom?:
    | 'projectContact'
    | 'contactFirstName'
    | 'projectDescription'
    | 'jobNumber'
    | 'client'
    | 'headContractor';
  /** Writing this field also sets documents.title. */
  syncsTitle?: boolean;
  /**
   * Bound to documents.sequence_number via set_document_sequence(), not to
   * header_fields -- so it is not part of the autosave payload.
   */
  bindsSequence?: boolean;
  /**
   * Bound straight to documents.title, with no header_fields key behind it.
   *
   * The letter reaches the title indirectly, through report_title + syncsTitle,
   * because that is how its data has always been shaped. A template with no
   * separate "title" concept binds the column itself rather than carrying a
   * duplicate of it in the blob.
   */
  bindsTitle?: boolean;
}

export const HEADER_FIELD_SCHEMAS: Record<DocumentChromeVariant, HeaderFieldDescriptor[]> = {
  letter: [
    { key: 'document_date', label: 'Document date', type: 'date' },
    { key: 'report_title', label: 'Report title', type: 'text', placeholder: 'Report title', syncsTitle: true },
    { key: 'contact_name', label: 'Attention', type: 'text', derivedFrom: 'projectContact' },
    { key: 'dear_name', label: 'Dear', type: 'text', derivedFrom: 'contactFirstName' },
  ],
  site_instruction: [
    { key: 'document_date', label: 'Date', type: 'date' },
    { label: 'SI No', type: 'text', bindsSequence: true },
    // The title is what the document is called, half of its number, and -- once
    // issued -- the description of the inspection record it writes. It has to
    // stay correctable after creation.
    { label: 'Instruction', type: 'text', placeholder: 'e.g. Footing Inspection', bindsTitle: true },
    { key: 'project', label: 'Project', type: 'text', derivedFrom: 'projectDescription' },
    { key: 'project_no', label: 'Project No', type: 'text', derivedFrom: 'jobNumber' },
    { key: 'client_architect', label: 'Client / Architect', type: 'text', derivedFrom: 'client' },
    { key: 'contractor', label: 'Contractor', type: 'text', derivedFrom: 'headContractor' },
    { key: 'method', label: 'Method', type: 'select', options: SI_METHOD_OPTIONS },
  ],
};

/**
 * Document instance
 */
export interface Document {
  id: string;
  template_id: string;
  project_id: number | null;
  stage_id: number | null;
  document_number: string;
  /**
   * Per-entity running number, allocated server-side by create_document and
   * changed only by set_document_sequence. Null for templates whose
   * numbering_pattern has no {sequence} token.
   */
  sequence_number: number | null;
  title: string | null;
  header_fields: DocumentHeaderFields;
  content: Block[];
  status: DocumentStatus;
  signatory_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  /**
   * Set by mark_document_sent. Null on a Sent document that predates the
   * Signed/Sent split -- those were marked Sent in bulk with no date recorded.
   */
  sent_at: string | null;
  sent_by: string | null;
}

/**
 * Document with joined data for list display
 */
export interface DocumentListItem {
  id: string;
  template_id: string;
  template_name: string;
  document_type: string;
  project_id: number | null;
  stage_id: number | null;
  stage_code: string | null;
  stage_description: string | null;
  job_number: string | null;
  project_description: string | null;
  client_name: string | null;
  document_number: string;
  title: string | null;
  status: string;
  signatory_name: string | null;
  created_by_name: string | null;
  document_date: string;   // The editable document date
  created_at: string;
  updated_at: string;
}

/**
 * Document version (for history)
 */
export interface DocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  title: string | null;
  header_fields: DocumentHeaderFields;
  content: Block[];
  status: string;
  signatory_id: string | null;
  created_by: string;
  created_by_name?: string;
  created_at: string;
}

/**
 * Document image record
 */
export interface DocumentImage {
  id: string;
  document_id: string;
  storage_path: string;
  filename: string | null;
  caption: string | null;
  display_order: number;
  uploaded_by: string | null;
  uploaded_at: string;
}

// =======================================
// FILTER TYPES
// =======================================

/**
 * Document filters for server-side filtering
 */
export interface DocumentFilters {
  search_term?: string | null;
  template_type_filter?: string | null;
  status_filter?: string[] | null;
  project_filter?: number | null;
  from_date?: string | null;
  to_date?: string | null;
  page_size?: number;
  page_offset?: number;
  sort_by?: string;
  sort_order?: string;
}

// =======================================
// CONSTANTS
// =======================================

/**
 * Default pagination settings
 */
export const DEFAULT_PAGE_SIZE = 25;
export const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Document status options
 */
export const DOCUMENT_STATUS_OPTIONS = [
  { id: 'Draft', label: 'Draft', value: 'Draft' },
  { id: 'Awaiting Approval', label: 'Awaiting Approval', value: 'Awaiting Approval' },
  { id: 'Signed', label: 'Signed', value: 'Signed' },
  { id: 'Sent', label: 'Sent', value: 'Sent' }
] as const;

/**
 * Document type options
 */
export const DOCUMENT_TYPE_OPTIONS = [
  { id: 'report', label: 'Report', value: 'report' },
  { id: 'site_instructions', label: 'Site Inspection Records', value: 'site_instructions' },
  { id: 'invoice', label: 'Invoice', value: 'invoice' }
] as const;

/**
 * Sort options for document list
 */
export const SORT_OPTIONS = [
  { id: 'created_at', label: 'Date Created', value: 'created_at' },
  { id: 'updated_at', label: 'Last Updated', value: 'updated_at' },
  { id: 'document_number', label: 'Document Number', value: 'document_number' },
  { id: 'title', label: 'Title', value: 'title' }
] as const;

// =======================================
// HELPER FUNCTIONS
// =======================================

/**
 * Generate a unique block ID
 */
export const generateBlockId = (): string => {
  return crypto.randomUUID();
};

/**
 * Create a new block with default data
 */
export const createBlock = (type: BlockType): Block => {
  const id = generateBlockId();
  
  switch (type) {
    case 'heading':
      return { id, type, data: { level: 2, text: '' } as HeadingBlockData };
    case 'paragraph':
      return { id, type, data: { content: '' } as ParagraphBlockData };
    case 'scope_list':
      return { 
        id, 
        type, 
        data: { 
          items: [{ id: generateBlockId(), text: '', indent: 0 }],
          showCheckboxes: true 
        } as ScopeListBlockData 
      };
    case 'bullet_list':
      return { id, type, data: { items: [''], ordered: false, style: 'bullet' } as BulletListBlockData };
    case 'table':
      return { 
        id, 
        type, 
        data: { 
          rows: [
            [{ content: '', header: true }, { content: '', header: true }],
            [{ content: '' }, { content: '' }]
          ],
          hasHeaderRow: true 
        } as TableBlockData 
      };
    case 'image':
      return { id, type, data: { 
        imageId: null, 
        storagePath: null, 
        caption: '', 
        alt: '',
        size: 'medium',
        alignment: 'center',
        layout: 'single',
        aspect: 'horizontal-4-3',
        images: []
      } as ImageBlockData };
    case 'image_appendix':
      return { 
        id, 
        type, 
        data: {
          title: 'Appendix - Photos',
          columns: 2,
          rows: 2,
          aspect: DEFAULT_APPENDIX_ASPECT,
          images: []
        } as ImageAppendixBlockData
      };
    case 'signature':
      return { 
        id, 
        type, 
        data: { 
          closing: 'Yours faithfully,',
          company: 'For Terpkos Engineering Pty Ltd'
        } as SignatureBlockData 
      };
    case 'instruction_list':
      return {
        id,
        type,
        data: {
          // Five rows, matching the printed form. Empty rows still render in
          // BOTH surfaces -- hiding them in the PDF only would change the
          // height the editor measured.
          rows: Array.from({ length: 5 }, () => ({
            id: generateBlockId(),
            blocks: [createBlock('paragraph')],
          })),
        } as InstructionListBlockData,
      };
    case 'drawing_list':
      // Empty until createDocument fills it from the stage's drawing set, or
      // the user presses Refresh. An empty list is a real state -- a stage with
      // no drawings yet -- so the block renders it rather than hiding.
      return { id, type, data: { rows: [], site_classification: null } as DrawingListBlockData };
    case 'qa_list':
      // Likewise empty until captureAsConstructed fills it from the job's
      // ticked RWC QA items, or the user presses Refresh.
      return { id, type, data: { rows: [] } as QaListBlockData };
    case 'table_appendix':
      // The inspection record's three columns and one empty row -- a table with
      // no rows would have nothing to click into. "Fill from photos" adds the
      // rest.
      return {
        id,
        type,
        data: {
          title: 'Appendix - Inspection Record',
          columns: createDefaultTableColumns(),
          rows: [blankTableRow()],
        } as TableAppendixBlockData,
      };
    case 'plan_appendix':
      return {
        id,
        type,
        data: {
          title: 'Appendix - Location Plan',
          labelSource: 'image',
          tableBlockId: null,
          pages: [blankPlanPage()],
        } as PlanAppendixBlockData,
      };
    case 'pdf_attachment':
      return {
        id,
        type,
        data: { title: '', storagePath: null, filename: '', pages: [] } as PdfAttachmentBlockData,
      };
    case 'blank_page':
      return { id, type, data: {} as BlankPageBlockData };
    case 'divider':
      return { id, type, data: { style: 'solid' } as DividerBlockData };
    case 'spacer':
      return { id, type, data: { height: 'medium' } as SpacerBlockData };
    default:
      return { id, type, data: {} as BlockData };
  }
};

/**
 * Compute the starting photo number for an image_appendix block.
 *
 * If the block has `continueNumbering` enabled, walks backwards through
 * preceding image_appendix blocks summing their image counts until it hits
 * an appendix that does NOT continue (the chain root) or the document start.
 * Otherwise returns 1.
 */
export const getAppendixStartNumber = (
  blocks: Block[],
  targetBlockId: string
): number => {
  const targetIndex = blocks.findIndex(b => b.id === targetBlockId);
  if (targetIndex === -1) return 1;

  const target = blocks[targetIndex];
  if (target.type !== 'image_appendix') return 1;
  const targetData = target.data as ImageAppendixBlockData;
  if (!targetData.continueNumbering) return 1;

  let count = 0;
  for (let i = targetIndex - 1; i >= 0; i--) {
    const block = blocks[i];
    if (block.type !== 'image_appendix') continue;
    const data = block.data as ImageAppendixBlockData;
    count += data.images?.length || 0;
    if (!data.continueNumbering) break;
  }
  return count + 1;
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Split a report-header date into its parts (e.g. "5" / "th" / " May 2026").
 *
 * Split rather than formatted, because both chromes superscript the ordinal
 * suffix and so have to draw it as its own element. See dateOrdinal.ts.
 */
export const formatDateLongParts = (dateString: string): LongDateParts => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-AU', { month: 'long' });
  return {
    day: String(day),
    ordinal: getDaySuffix(day),
    rest: ` ${month} ${date.getFullYear()}`,
  };
};

/**
 * Get ordinal suffix for day
 */
const getDaySuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

// NOTE: a client-side generateDocumentNumber() used to live here. It was dead
// code (no call sites) and it padded {sequence} to two digits, which no longer
// matches anything. Numbering is now allocated server-side by create_document()
// and changed only by set_document_sequence(), both under an advisory lock --
// a client-side MAX()+1 races, and the number has a unique index behind it.
// Do not reintroduce a client-side expander.

/**
 * Create empty header fields for a chrome.
 *
 * Used both to seed a new document and as the merge base when reading an older
 * one, so every key a renderer touches is always present as a string.
 */
export const createEmptyHeaderFields = (
  chrome: DocumentChromeVariant = 'letter'
): DocumentHeaderFields => {
  const today = new Date().toISOString().split('T')[0];

  if (chrome === 'site_instruction') {
    // Everything except the date starts blank, which means "derive live from
    // the project". They are filled in for good when the document is issued.
    return {
      document_date: today,
      project: '',
      project_no: '',
      client_architect: '',
      contractor: '',
      method: SI_METHOD_DEFAULT,
    };
  }

  return {
    document_date: today,
    contact_name: '',
    dear_name: '',
    client_street: '',
    client_city: '',
    client_state: '',
    client_postcode: '',
    project_address: '',
    report_title: ''
  };
};

/**
 * The header fields a document renders from: its stored values over the
 * chrome's defaults.
 *
 * Both the editor canvas and the PDF export must use THIS, not the raw column.
 * A document stores only the keys that existed when it was created, so a field
 * added later is absent from every older row -- and a surface that reads the
 * column directly would show a blank where the other shows the default. That is
 * a parity break, and the kind that only appears on old documents.
 */
export const mergeHeaderFields = (
  stored: Partial<DocumentHeaderFields> | null | undefined,
  chrome: DocumentChromeVariant,
  createdAt?: string | null
): DocumentHeaderFields => ({
  ...createEmptyHeaderFields(chrome),
  ...stored,
  document_date:
    stored?.document_date
    || createdAt?.split('T')[0]
    || new Date().toISOString().split('T')[0],
});

/**
 * The title a new document of this type starts with.
 *
 * Only for templates whose title is standardised: a design certificate is always
 * a certificate of design compliance, so typing it out each time invites drift
 * in the one line that names the document. It is still editable at creation, and
 * it flows to `report_title` (which carries syncsTitle) so the Re: block and the
 * document's own name stay one value.
 */
export const DEFAULT_DOCUMENT_TITLES: Record<string, string> = {
  rwd_design_certificate: 'CERTIFICATE OF DESIGN COMPLIANCE \u2013 RETAINING WALLS',
  rwc_as_constructed_certificate: 'AS CONSTRUCTED RETAINING WALL CERTIFICATION',
};

/**
 * Templates whose Re: block names the job but not its address.
 *
 * The RWC As Constructed Certificate's originals give the job and the title
 * only -- the certificate is about works, and the address is already in the
 * client block above it. Held here rather than in either surface because
 * DocumentChrome and DocumentPDF both draw that block, and a line one of them
 * shows and the other hides moves the whole body 12.65pt.
 */
export const TEMPLATES_WITHOUT_PROJECT_ADDRESS: ReadonlySet<string> = new Set([
  'rwc_as_constructed_certificate',
]);

/**
 * Templates that close on the company's trustee line instead of page numbers.
 *
 * The certificates are letters, and their originals were never numbered: no
 * page carries "Page X of Y", and the LAST page carries the trustee line
 * (CERTIFICATE_FOOTER_SRC) bottom right, inside the bottom margin. Held here
 * for the same reason as the set above: the editor's page and DocumentPDF each
 * draw the footer, so both read this one list. Both footers are absolutely
 * positioned on both surfaces, so swapping one for the other moves nothing.
 */
export const TEMPLATES_WITH_CERTIFICATE_FOOTER: ReadonlySet<string> = new Set([
  'rwd_design_certificate',
  'rwc_as_constructed_certificate',
]);

/**
 * "Terpkos Engineering Pty Ltd ACN ... as trustee for the TEG Trust ABN ..."
 * under a short lime rule, in client/public. An image, like the letterhead, so
 * it keeps the brand face on both surfaces without a font to register.
 */
export const CERTIFICATE_FOOTER_SRC = 'certificate-footer.png';

/**
 * The job number as a document prints it: "16151", or "16151.2" for a stage
 * document. One rule for the editor's page, DocumentPDF and the export's file
 * name, so the name on the file is the number on the page.
 */
export const displayJobNumberOf = (
  projectData: { job_number: number | null; stage?: { stage_id: string } | null } | null | undefined
): string =>
  projectData?.job_number != null
    ? (projectData.stage?.stage_id
        ? `${projectData.job_number}.${projectData.stage.stage_id}`
        : String(projectData.job_number))
    : '—';

/**
 * The project line as a document prints it: "Nannup Walls", or
 * "Nannup Walls - Stage 1" for a stage document -- the job first, so a stage's
 * certificate reads as the job it belongs to. One rule for the editor's page
 * and DocumentPDF; snapshot_document_header writes the same form into a site
 * instruction's header when it is issued.
 */
export const displayProjectDescriptionOf = (
  projectData: { description: string | null; stage?: { stage_description: string } | null } | null | undefined
): string =>
  projectData?.stage?.stage_description && projectData?.description
    ? `${projectData.description} - ${projectData.stage.stage_description}`
    : (projectData?.description || '—');

/**
 * Exported file names for templates named after the job rather than by their
 * document number. `date` is the date printed on the document, as dd.mm.yy --
 * a reissue next month is a different file, not an overwrite of this one.
 */
const EXPORT_FILE_NAMES: Record<string, (p: { jobNumber: string; date: string }) => string> = {
  rwd_design_certificate: ({ jobNumber, date }) =>
    `${jobNumber} - Cert - ${date}`,
  rwc_as_constructed_certificate: ({ jobNumber, date }) =>
    `${jobNumber} - As Constructed Certificate - ${date}`,
};

/**
 * The exported PDF's file name, without the extension. Every template not
 * listed above is named by its document number, as it always has been.
 * `documentDate` is ISO ('2026-10-07', or a full timestamp).
 */
export const exportFileName = (
  templateCode: string | null | undefined,
  documentNumber: string,
  jobNumber: string,
  documentDate: string
): string => {
  const named = EXPORT_FILE_NAMES[templateCode ?? ''];
  if (!named) return documentNumber;
  // Split rather than parsed: new Date('2026-10-07') is UTC midnight, which is
  // the previous day anywhere west of Greenwich.
  const [y, m, d] = documentDate.split('T')[0].split('-');
  return named({ jobNumber, date: `${d}.${m}.${y.slice(2)}` });
};

/**
 * Stands in for the design-criteria sentence between createInitialBlocks and
 * captureDesignCriteria, which writes the sentence composed from the captured
 * drawings in its place. Never reaches the page: creation fails outright if
 * the drawings cannot be read.
 */
export const DESIGN_CRITERIA_TOKEN = '{{design_criteria}}';

export const DRAWINGS_INTRO = 'The drawings relevant to this Certification are listed below:';

/**
 * The design-criteria sentence as a paragraph's content. Paragraph content is
 * html, so the sentence is escaped -- and a refresh recognises an unedited
 * paragraph by comparing against exactly this.
 */
export const designCriteriaParagraph = (data: DrawingListBlockData): string =>
  escapeHtml(designCriteriaSentence(data));

/**
 * The RWD Design Certificate's body, as the Word original reads it.
 *
 * Every line is an ordinary editable block, as on the As Constructed
 * Certificate: standardised by default, but an engineer can adjust a one-off
 * without a code change. Only the drawing rows are captured (see
 * DrawingListBlockData); the design-criteria sentence is composed from them
 * once at creation and is prose from then on, followed by a Refresh only while
 * nobody has edited it.
 */
const DESIGN_CERTIFICATE_STANDARDS = [
  'AS/NZS 1170.1:2002 – Permanent, imposed and other actions',
  'AS/NZS 1170.2:2021 – Wind actions',
  'AS 2870-2011 – Residential slabs and footings',
  'AS 3700-2018 – Masonry Structures',
  'AS 4678-2002 – Earth Retaining Structures',
];

const createDesignCertificateBlocks = (): Block[] => {
  const paragraph = (content: string): Block => {
    const b = createBlock('paragraph');
    (b.data as ParagraphBlockData).content = content;
    return b;
  };

  const standards = createBlock('bullet_list');
  (standards.data as BulletListBlockData).items = [...DESIGN_CERTIFICATE_STANDARDS];

  // Rows only: its lead-in is the two paragraphs above it.
  const drawings = createBlock('drawing_list');
  (drawings.data as DrawingListBlockData).showsLeadIn = false;

  return [
    paragraph(
      'We confirm that the retaining walls on the above project have been designed in ' +
      'accordance with and comply with the following relevant Australian Standards:'
    ),
    standards,
    paragraph(DESIGN_CRITERIA_TOKEN),
    paragraph(DRAWINGS_INTRO),
    drawings,
    paragraph('Please find attached PDF signed copies as requested.'),
    paragraph('Please contact us should you have any further requirements.'),
    createBlock('signature'),
  ];
};

/**
 * A Design Certificate draft from before its lead-in became paragraphs,
 * brought to the current shape: the design-criteria sentence and "listed
 * below:" lifted out of the drawing block into ordinary editable paragraphs
 * above it, worded exactly as the block drew them, and the block set to rows
 * only. Returns the blocks unchanged when there is nothing to lift.
 *
 * DRAFTS ONLY -- the caller checks. An issued certificate must go on saying
 * what it was issued with, in the shape it was issued in.
 *
 * Lays out identically: the block's blank line under each sentence is the
 * paragraphs' default spacing, and the first paragraph takes over whatever
 * space the block had above it.
 */
export const liftDrawingLeadIn = (blocks: Block[]): Block[] => {
  const index = blocks.findIndex(b => b.type === 'drawing_list');
  if (index === -1) return blocks;
  const drawings = blocks[index];
  const data = drawings.data as DrawingListBlockData;
  if (data.showsLeadIn === false) return blocks;

  const spacing = getBlockSpacing(drawings);
  const paragraph = (content: string, above: boolean): Block => ({
    ...createBlock('paragraph'),
    data: { content } as ParagraphBlockData,
    spacing: { above, below: true },
  });

  return [
    ...blocks.slice(0, index),
    paragraph(designCriteriaParagraph(data), spacing.above),
    paragraph(escapeHtml(DRAWINGS_INTRO), true),
    { ...drawings, data: { ...data, showsLeadIn: false }, spacing: { above: true, below: spacing.below } },
    ...blocks.slice(index + 1),
  ];
};

/**
 * The RWC As Constructed Certificate's body, as the Word originals read it.
 *
 * Four values come from the job -- how many site visits were made, the
 * engineering consultant, the client, and the QA items relied on. The first
 * three are ordinary words inside ordinary sentences, so they are substituted
 * ONCE at creation (see captureAsConstructed) and are editable prose from then
 * on. Only the QA list, which is a repeating structure, has a Refresh: a
 * refresh that rewrote a sentence the engineer may have reworded is the
 * destructive shape this codebase has already been bitten by once.
 *
 * The tokens exist only between createInitialBlocks and captureAsConstructed.
 * Every one has a visible fallback, so a token can never reach the page.
 */
export const AS_CONSTRUCTED_TOKENS = {
  /** "two site visits" / "one site visit" / "[enter number] site visits" */
  visits: '{{site_visits}}',
  /** "inspections" / "inspection" -- agrees with the visit count above. */
  inspectionNoun: '{{site_inspection_noun}}',
  /** The retaining standard, which names the drawings being certified against. */
  consultant: '{{consultant}}',
  /** Named as the party that verified the works we did not inspect. */
  client: '{{client}}',
};

/**
 * HTML-escape a value going into a paragraph's content.
 *
 * Paragraph content IS html -- the contenteditable's innerHTML -- so a client
 * called "Cossill & Webley" written in raw would produce a stray entity in the
 * editor and a broken run in the PDF, which decodes &amp; on the way out.
 */
export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const NUMBER_WORDS = [
  'zero', 'one', 'two', 'three', 'four', 'five',
  'six', 'seven', 'eight', 'nine', 'ten',
];

/**
 * "two", "eleven" -> "11". The certificates spell small numbers out, which is
 * how the letters have always read; past ten the word stops helping.
 */
export const numberInWords = (n: number): string =>
  n >= 0 && n < NUMBER_WORDS.length ? NUMBER_WORDS[n] : String(n);

/**
 * "two site visits" / "one site visit". Null when the job has no inspection
 * records at all -- the author is asked rather than told "zero site visits".
 */
export const siteVisitPhrase = (count: number | null): string =>
  count && count > 0
    ? `${numberInWords(count)} site visit${count === 1 ? '' : 's'}`
    : `${VISIT_COUNT_PLACEHOLDER} site visits`;

const AS_CONSTRUCTED_CLOSING =
  'We certify that all limestone retaining wall works have been completed in ' +
  'accordance with the structural requirements of the contract drawings. Note ' +
  'fencing is not covered by this as constructed certification as it was ' +
  'completed by others.';

const createAsConstructedBlocks = (): Block[] => {
  const paragraph = (content: string): Block => {
    const b = createBlock('paragraph');
    (b.data as ParagraphBlockData).content = content;
    return b;
  };

  return [
    paragraph('Following recently forwarded QA certificates we advise as follows.'),
    // RWC has no drawing set of its own -- the drawings belong to the civil
    // consultant, not to us -- so they are typed into the end of this sentence
    // rather than captured.
    paragraph(
      'In accordance with our agreed scope of works we have carried out ' +
      `${AS_CONSTRUCTED_TOKENS.visits} during construction of the retaining walls ` +
      'for the above project to check compliance in accordance with ' +
      `${AS_CONSTRUCTED_TOKENS.consultant} drawings: ${DRAWINGS_PLACEHOLDER}`
    ),
    paragraph(
      'It is important to note that our certification assumes that ' +
      `${AS_CONSTRUCTED_TOKENS.client} has verified all works not inspected by our ` +
      'office representative for compliance with the contract documentation and ' +
      'general project requirements.'
    ),
    paragraph(
      `On the basis of our random site ${AS_CONSTRUCTED_TOKENS.inspectionNoun}, ` +
      'satisfactory completion of our site instruction and the following ' +
      'information provided by your office:'
    ),
    createBlock('qa_list'),
    paragraph(AS_CONSTRUCTED_CLOSING),
    createBlock('signature'),
  ];
};

/**
 * The blocks a new document starts with.
 *
 * Deliberately NOT read from document_templates.blocks: block shape is defined
 * by the types in this file, and a copy in the database would go stale the next
 * time a block type changes.
 */
export const createInitialBlocks = (chrome: DocumentChromeVariant, templateCode?: string | null): Block[] => {
  // Keyed on the template CODE, not the chrome: the design certificate reuses
  // the letter chrome the Standard Report draws, but a report starts blank and
  // a certificate starts with its standardised wording.
  if (templateCode === 'rwd_design_certificate') return createDesignCertificateBlocks();
  if (templateCode === 'rwc_as_constructed_certificate') return createAsConstructedBlocks();

  if (chrome === 'site_instruction') {
    const signature = createBlock('signature');
    const data = signature.data as SignatureBlockData;
    // No "Yours faithfully," on a site instruction -- it is an instruction, not
    // a letter. An empty closing is handled explicitly by both the editor block
    // and DocumentPDF; see signatureSpaceLines().
    data.closing = '';
    // Labels are seeded INTO the document so an instruction issued years ago
    // keeps the distribution list it was issued with.
    data.distribution = [
      { id: generateBlockId(), label: 'Contractor', checked: false },
      { id: generateBlockId(), label: 'Client / Architect', checked: false },
      { id: generateBlockId(), label: 'File', checked: false },
    ];
    return [createBlock('instruction_list'), signature];
  }
  return [];
};
